<template>
  <div class="bcb">
    <Button
      v-if="confirmable && confirmed"
      variant="ghost"
      size="sm"
      icon="fas fa-pen"
      data-testid="besoin-reopen"
      @click="$emit('reopen')"
    >
      Modifier
    </Button>
    <span v-else-if="confirmable && refused" class="bcb-hint is-refused" role="alert" data-testid="besoin-refused">
      <i class="fas fa-circle-exclamation" aria-hidden="true"></i>
      {{ refusedText(refused) }}
    </span>

    <!-- UN SEUL GESTE : confirmer ET passer à la suite. La flèche vit dans le
         slot et non dans `icon` : `Button` rend toujours son icône AVANT le
         libellé, et une flèche « suivant » posée à gauche du mot désigne
         l'inverse de ce qu'elle fait. -->
    <Button
      v-if="confirmable && !confirmed"
      variant="primary"
      size="sm"
      class="bcb-next"
      :data-testid="`besoin-confirm-${sectionKey}`"
      @click="$emit('confirm')"
    >
      {{ nextLabel ? 'Confirmer et continuer' : 'Confirmer' }}
      <i v-if="nextLabel" class="fas fa-arrow-right" aria-hidden="true"></i>
    </Button>
    <Button
      v-else-if="nextLabel"
      variant="secondary"
      size="sm"
      class="bcb-next"
      data-testid="besoin-next"
      @click="$emit('next')"
    >
      Continuer
      <i class="fas fa-arrow-right" aria-hidden="true"></i>
    </Button>
  </div>
</template>

<script setup>
/**
 * LE GESTE CENTRAL DE LA FENÊTRE, À DEMEURE SOUS LE PANNEAU.
 *
 * Il y avait deux commandes côte à côte : la case « Infos confirmées » et le
 * bouton « Suivant ». Le commercial les enchaînait à chaque section, au
 * téléphone. Il n'y en a plus qu'une : « Confirmer et continuer » verrouille la
 * section et ouvre la suivante.
 *
 * Une section confirmée se relit en lecture seule ; « Modifier » la rouvre —
 * c'était auparavant le rôle de la case, qu'on décochait. « Continuer » seul
 * reste pour parcourir sans confirmer : section déjà confirmée, bloc de
 * lecture, devis signé.
 *
 * PEU DE TEXTE (retour Figma du 2026-09-11, « un clic suffit et pas trop de
 * texte ») : ni phrase d'aide, ni « Section confirmée » écrit en toutes
 * lettres — le badge du titre et la coche du plan le disent déjà — ni nom de
 * la section suivante dans le bouton, que le plan affiche juste à côté. Seul
 * le refus s'écrit : c'est la seule chose que le commercial ne voit pas
 * ailleurs.
 *
 * PRÉSENTATIONNEL : des props entrent, `confirm`, `reopen` et `next` sortent.
 * C'est l'écran qui décide si la confirmation est acceptée ; refusée, la barre
 * dit combien de réponses obligatoires manquent (`refused`).
 */
import { Button } from '../ui'
import { refusedText } from '../../composables/quotes/useBesoinConfirmGuard.js'

defineProps({
  /** Section confirmée ? */
  confirmed: {
    type: Boolean,
    default: false
  },
  /** Identifiant de section, pour le `data-testid` (tests navigateur). */
  sectionKey: {
    type: String,
    required: true
  },
  /**
   * Faux pour l'argumentaire (bloc de lecture), le point de vigilance
   * (calculé), une section sans question, et partout sur un devis signé :
   * il n'y a alors rien à confirmer.
   */
  confirmable: {
    type: Boolean,
    default: true
  },
  /**
   * Réponses obligatoires qui ont fait refuser la confirmation : zéro tant
   * qu'on n'a pas essayé. Le refus se dit ICI, là où l'on vient de cliquer.
   */
  refused: {
    type: Number,
    default: 0
  },
  /** Libellé de la section suivante, ou null sur la dernière. */
  nextLabel: {
    type: String,
    default: null
  }
})

defineEmits(['confirm', 'reopen', 'next'])
</script>

<style scoped>
.bcb {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  padding: var(--spacing-3) var(--spacing-6);
  border-top: 1px solid var(--color-border-light);
  background: var(--color-bg-primary);
}

.bcb-hint {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-2);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.bcb-hint.is-refused {
  color: var(--color-danger-dark);
  font-weight: var(--font-weight-medium);
}

.bcb-next {
  margin-left: auto;
}

/* Sur téléphone, la barre RESTE en ligne : empilée, elle prenait 169 px sur
   les 736 de la feuille et ne laissait plus de quoi saisir. Le bouton prend
   la première ligne, la phrase d'aide passe dessous en petit — elle
   explique, elle ne commande pas. */
@media (max-width: 768px) {
  .bcb {
    flex-wrap: wrap;
    gap: var(--spacing-2);
    padding: var(--spacing-2) var(--spacing-md);
  }

  .bcb-hint {
    order: 3;
    flex: 1 1 100%;
    font-size: var(--font-size-xs);
    line-height: 1.35;
  }

  .bcb-next {
    order: 2;
  }
}
</style>
